import {
  Typenames,
  VlocActive,
  VlocApplicationDenied,
  VlocDestination,
  VlocDestinationData,
  VlocLostEligibility,
  VlocNotEligibleToQualify,
  VlocNotPreQualified,
  VlocNotPreQualifiedCreditFrozen,
  VlocPendingDisbursement,
  VlocPreQualified,
  VlocStartApplication,
  VlocStartNewDisbursement,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useFocusEffect } from 'expo-router';
import ActiveDetailsScreen from 'features/lineOfCredit/screens/activeDetailsScreen/ActiveDetailsScreen';
import EligibilityScreen from 'features/lineOfCredit/screens/eligibility/EligibilityScreen';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import HardPullDeclineAndCreditFrozenScreen from 'features/lineOfCredit/screens/hardPullDeclineAndCreditFrozen/HardPullDeclineAndCreditFrozenScreen';
import useVaroLineOfCreditWhereToGo from 'features/lineOfCredit/screens/lineOfCreditEntryPoint/hooks/useVaroLineOfCreditWhereToGo';
import LostEligibilityScreen from 'features/lineOfCredit/screens/lostEligibility/LostEligibilityScreen';
import NotQualifiedScreen from 'features/lineOfCredit/screens/notQualified/NotQualifiedScreen';
import PrequalApprovedScreen from 'features/lineOfCredit/screens/preApproved/PrequalApprovedScreen';
import TakeOutScreen from 'features/lineOfCredit/screens/takeOut/TakeOutScreen';
import VlocIsOnItsWayScreen from 'features/lineOfCredit/screens/vlocIsOnItsWay/VlocIsOnItsWayScreen';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const hasDestination = (
  result: VlocDestinationData,
): result is Exclude<VlocDestinationData, { __typename?: 'GoToError' }> =>
  'type' in result;

const isVlocActive = (result: VlocDestinationData): result is VlocActive =>
  hasDestination(result) && result.type === VlocDestination.VlocActive;

const isVlocLostEligibility = (
  result: VlocDestinationData,
): result is VlocLostEligibility =>
  hasDestination(result) && result.type === VlocDestination.VlocLostEligibility;

const isVlocNotPreQualified = (
  result: VlocDestinationData,
): result is VlocNotPreQualified =>
  hasDestination(result) && result.type === VlocDestination.VlocNotPreQualified;

const isVlocStartApplication = (
  result: VlocDestinationData,
): result is VlocStartApplication =>
  hasDestination(result) &&
  result.type === VlocDestination.VlocStartApplication;

const isVlocNotEligibleToQualify = (
  result: VlocDestinationData,
): result is VlocNotEligibleToQualify =>
  hasDestination(result) &&
  result.type === VlocDestination.VlocNotEligibleToQualify;

const isVlocCreditFrozen = (
  result: VlocDestinationData,
): result is VlocNotPreQualifiedCreditFrozen =>
  hasDestination(result) &&
  result.type === VlocDestination.VlocNotPreQualifiedCreditFrozen;

const isVlocApplicationDenied = (
  result: VlocDestinationData,
): result is VlocApplicationDenied =>
  hasDestination(result) &&
  result.type === VlocDestination.VlocApplicationDenied;

const isVlocPreQualified = (
  result: VlocDestinationData,
): result is VlocPreQualified =>
  hasDestination(result) && result.type === VlocDestination.VlocPreQualified;

const isVlocPendingDisbursement = (
  result: VlocDestinationData,
): result is VlocPendingDisbursement =>
  hasDestination(result) &&
  result.type === VlocDestination.VlocPendingDisbursement;

const isVlocStartNewDisbursement = (
  result: VlocDestinationData,
): result is VlocStartNewDisbursement =>
  hasDestination(result) &&
  result.type === VlocDestination.VlocStartNewDisbursement;

const ResponseComponent = ({ result }: { result?: VlocDestinationData }) => {
  if (!result) {
    return null;
  }

  let screen: React.ReactNode = null;
  if (isVlocActive(result)) {
    screen = <ActiveDetailsScreen />;
  } else if (isVlocLostEligibility(result)) {
    const body = result.vlocLostEligibilityBody;
    screen = (
      <LostEligibilityScreen
        data={{
          vlocLostEligibilityBody: {
            title: body?.title,
            subTitle: body?.subTitle,
            items: body?.items.map((item) => ({
              title: item.title,
              subTitle: item.subTitle,
              options: item.options,
              disclaimer1: item.disclaimer1,
              disclaimer2: item.disclaimer2,
            })),
          },
        }}
      />
    );
  } else if (isVlocNotPreQualified(result)) {
    const notQualified = result.vlocNotQualified;
    const noBelieve = result.vlocNoBelieve;
    screen = (
      <NotQualifiedScreen
        data={{
          vlocNotQualified: {
            title: notQualified?.title,
            subTitle: notQualified?.subTitle,
            description: notQualified?.description,
            optionItems: notQualified?.optionItems,
            footer: notQualified?.footer,
          },
          vlocNoBelieve: noBelieve
            ? {
                title: noBelieve.title,
                subTitle: noBelieve.subTitle,
                description: noBelieve.description,
                articleLink: noBelieve.articleLink
                  ? {
                      id: noBelieve.articleLink.id,
                      copy: noBelieve.articleLink.copy,
                    }
                  : undefined,
              }
            : null,
        }}
      />
    );
  } else if (
    isVlocStartApplication(result) ||
    isVlocNotEligibleToQualify(result)
  ) {
    screen = <EligibilityScreen data={result} />;
  } else if (isVlocCreditFrozen(result) || isVlocApplicationDenied(result)) {
    screen = <HardPullDeclineAndCreditFrozenScreen data={result} />;
  } else if (isVlocPreQualified(result)) {
    screen = <PrequalApprovedScreen data={result} />;
  } else if (isVlocPendingDisbursement(result)) {
    screen = <VlocIsOnItsWayScreen data={result} />;
  } else if (isVlocStartNewDisbursement(result)) {
    screen = <TakeOutScreen data={result} />;
  }

  return screen ? (
    <View style={styles.container} testID="render-component">
      {screen}
    </View>
  ) : null;
};

const LineOfCreditEntryPoint = () => {
  const { fetching, result, error, refetch } = useVaroLineOfCreditWhereToGo();
  const { t: translatedText } = useTranslation('vloc');

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('navigationTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={
          <View>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error || result?.__typename === Typenames.GoToError ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-lineOfCreditEntryPoint"
              trackingAnalyticsId="line-of-credit-entrypoint-error-tracking"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_GENERAL,
              }}
              insetBottomSafeArea
            />
          ) : null
        }
      />
      <ResponseComponent result={result ?? undefined} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default LineOfCreditEntryPoint;
